import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, Handshake, ShieldCheck, Users, FileSearch } from "lucide-react";
import SectionHead from "../components/site/SectionHead";
import { useSite } from "../context/SiteContext";

const VALUES = [
  { icon: Users, title: "Choice Through a Partner Network", text: "We work with a network of banking and NBFC lending partners so customers can explore multiple suitable options through a single, guided window." },
  { icon: Handshake, title: "Human Expertise, Not Just Forms", text: "Every enquiry is handled by a trained relationship manager who understands lending criteria and explains options in plain language." },
  { icon: FileSearch, title: "Transparent Process", text: "We tell you upfront what's realistic for your profile, what documents are needed and what each stage involves. No exaggerated promises." },
  { icon: ShieldCheck, title: "Your Data, Protected", text: "Personal information and documents are handled under strict access controls and used only to evaluate and facilitate your request." },
];

export default function About() {
  const { settings } = useSite();
  return (
    <div data-testid="about-page">
      <section className="container-site py-14 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">About BharatBest Fintech</p>
            <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.08] text-brand-dark" data-testid="about-title">
              A Smarter Way to Find the <span className="text-brand-red">Right Loan</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-brand-muted leading-relaxed">
              {settings.company_name} is a digital loan marketplace and financial services intermediary. We help individuals and businesses explore, compare and apply for loan products offered by our network of banking and NBFC lending partners — with professional guidance from enquiry to application.
            </p>
            <p className="mt-4 text-sm text-brand-muted leading-relaxed">
              We are not a bank or NBFC, and we do not make lending decisions. Final approval, rates, tenure and terms are determined by the respective lending partner.
            </p>
          </div>
          <div className="lg:col-span-6">
            <img src="https://images.unsplash.com/photo-1781246081182-b8e658662ebd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="BharatBest advisors discussing loan options with a customer" className="rounded-3xl w-full h-[320px] lg:h-[440px] object-cover" data-testid="about-image" />
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-black/5 py-16 lg:py-24">
        <div className="container-site grid lg:grid-cols-2 gap-8">
          <div className="card-site p-8 sm:p-10">
            <Target size={26} className="text-brand-red" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-brand-dark">Our Mission</h2>
            <p className="mt-3 text-sm md:text-base text-brand-muted leading-relaxed">To make borrowing clear, comparable and guided — so every customer can approach the right lender with the right preparation, instead of applying blindly and facing avoidable rejections.</p>
          </div>
          <div className="card-site p-8 sm:p-10">
            <Eye size={26} className="text-brand-gold" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-brand-dark">Our Vision</h2>
            <p className="mt-3 text-sm md:text-base text-brand-muted leading-relaxed">To become India's most trusted loan marketplace — known for honest advice, transparent processes and genuine expertise across every loan category we serve.</p>
          </div>
        </div>
      </section>

      <section className="container-site py-16 lg:py-24">
        <SectionHead eyebrow="How We Help" title="Why Customers Choose BharatBest"
          subtitle="Everything we do is designed around one goal: getting your application in front of the right lender, the right way." />
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {VALUES.map((v, i) => (
            <div key={i} className="card-site p-8" data-testid={`about-value-${i}`}>
              <v.icon size={24} className="text-brand-red" />
              <h3 className="mt-4 font-heading text-lg font-bold text-brand-dark">{v.title}</h3>
              <p className="mt-2 text-sm text-brand-muted leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 text-center">
          <Link to="/apply" data-testid="about-apply-btn" className="btn-primary">Start Your Application <ArrowRight size={15} /></Link>
        </div>
      </section>
    </div>
  );
}
